import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiCalendar } from 'react-icons/fi';
import Card from '../common/Card';
import Button from '../common/Button';
import { formatDateRange, stripHtml, truncate } from '../../utils/helpers';
import { CardSkeleton } from '../common/Loading';
import SafeHTML from '../common/SafeHTML';

export default function FeaturedAnnouncements({ announcements = [], loading }) {
  if (loading) {
    return (
      <section className="section bg-white">
        <div className="container-wide">
          <div className="h-12 w-64 bg-gray-200 rounded mx-auto mb-12 animate-pulse" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        </div>
      </section>
    );
  }
  
  if (!announcements.length) {
    return null;
  }
  
  return (
    <section className="section bg-white">
      <div className="container-wide">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="section-title"
        >
          Latest Announcements
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {announcements.map((announcement, index) => (
            <motion.div
              key={announcement.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card 
                to={`/announcements/${announcement.slug}`}
                className="h-full flex flex-col"
              >
                {/* Date Badge */}
                {announcement.event_date && (
                  <div className="flex items-center gap-2 text-sm text-primary/60 mb-3">
                    <FiCalendar className="w-4 h-4" />
                    <span>
                      {formatDateRange(announcement.event_date, announcement.event_end_date)}
                    </span>
                  </div>
                )}
                
                {/* Title */}
                <h3 className="text-xl font-semibold text-primary mb-3 group-hover:text-accent transition-colors">
                  {announcement.title}
                </h3>
                
                {/* Description */}
                <SafeHTML
                  html={truncate(stripHtml(announcement.excerpt || announcement.content), 120)}
                  as="p"
                  className="text-gray-600 flex-grow mb-4"
                />
                
                {/* Link */}
                <span className="inline-flex items-center gap-1 text-primary font-medium text-sm group">
                  Learn More
                  <FiArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Card>
            </motion.div>
          ))}
        </div>
        
        {/* View All Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Button to="/announcements" variant="secondary">
            View All Announcements
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
